import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Button,
  Grid,
  Stack,
  Text,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useAccount, useSession, useSnackbar } from '@ltvco/refresh-lib/v1';
import {
  AuthRequiredModal,
  buildUserListingBody,
  buildUserListingUpdateBody,
  defaultValuesForReset,
  FilterHub,
  FilterTags,
  generateYearsList,
  getResultsSummaryParts,
  MobileFilter,
  resolveSaveAction,
  ResultsGrid,
  SaveButtonRoot,
  SaveButtonText,
  SaveListingButton,
  SaveSearchButton,
  CompareVehiclesBar,
  CompareVehiclesProvider,
  useCompareVehicles,
  useMarketplaceFacets,
  useMarketplaceListings,
  useSavedSearches,
  useUserListings,
  type MarketplaceListing,
  type MarketplaceSearchParams,
} from '@ltvco/refresh-lib/v2';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  Share,
  Tune,
} from '@mui/icons-material';
import { useContext, useEffect, useState } from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import { BumperShowroomAd } from '../../components/Marketplace/ads/BumperShowroomAd';
import { CarwiserAd } from '../../components/Marketplace/ads/CarwiserAd';
import { VinSearchAd } from '../../components/Marketplace/ads/VinSearchAd';

import {
  marketplaceQueryConfig,
  toSearchParams,
  type MarketplaceQueryValues,
} from './marketplaceQueryParams';

const availableYears = generateYearsList();

/**
 * Seller-type pill options, ported from the Ember `filter/hub` component's
 * `sellerTypes` list — adds the Bumper marketplace option on top of
 * `FilterHub`'s brand-agnostic default.
 */
const SELLER_TYPES = [
  { value: '', visibleName: 'Dealership' },
  { value: 'private', visibleName: 'Private party' },
  { value: 'auction', visibleName: 'Auction' },
  { value: 'bumper', visibleName: 'Bumper' },
];

const BUMPER_DOMAIN = 'https://www.bumper.com';
const BUILDING_REPORT_PATH = 'lp/8dc2dc/3/building-report';
const SUBSCRIBE_URL = 'https://www.bumper.com/lp/8b3bc4/4/subscribe';

/**
 * Absolute URL for a listing's detail page. The router basename must be
 * prepended manually since this is used both for in-app navigation via
 * `window.open` (which bypasses React Router) and for sharing outside the app.
 */
function getPathPrefix(): string {
  const rawPrefix =
    (import.meta.env.VITE_PATH_PREFIX as string | undefined) ?? '';
  return rawPrefix && !rawPrefix.startsWith('/') ? `/${rawPrefix}` : rawPrefix;
}

function buildListingUrl(listingId: string): string {
  return `${window.location.origin}${getPathPrefix()}/marketplace/listing/${listingId}`;
}

function buildReactivationUrl(): string {
  return `${window.location.origin}${getPathPrefix()}/reactivation`;
}

/**
 * Opens a listing's detail page in a new tab (matching the Ember
 * `openListingInNewTab`).
 */
function openListingInNewTab(listingId: string): void {
  window.open(buildListingUrl(listingId), '_blank', 'noopener');
}

/**
 * Vehicle-history-report URL for a listing's VIN, matching the
 * authenticated/unauthenticated split used elsewhere (e.g.
 * `MarketplaceVehicleDetail`'s `openVinReport`). Paused accounts are routed
 * to the in-app reactivation page instead of the report itself, since a
 * paused account can't pull a report until it's reactivated.
 */
function getVehicleReportUrl(
  vin: string,
  isAuthenticated: boolean,
  isPaused: boolean,
): string {
  if (isAuthenticated && isPaused) {
    return buildReactivationUrl();
  }
  const base = isAuthenticated
    ? `${BUMPER_DOMAIN}/rf/report/vehicle?`
    : `${BUMPER_DOMAIN}/${BUILDING_REPORT_PATH}?`;
  return `${base}vin=${vin}`;
}

/**
 * Normalizes a search-criteria object into a stable comparison key so
 * equivalent searches match regardless of key order: drops nullish, empty, and
 * empty-array values, then sorts the remaining entries by key.
 */
function toSearchCriteriaKey(criteria: object): string {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(criteria)
        .filter(
          ([, v]) =>
            v !== null &&
            v !== undefined &&
            v !== '' &&
            !(Array.isArray(v) && v.length === 0),
        )
        .sort(([a], [b]) => a.localeCompare(b)),
    ),
  );
}

/**
 * Public marketplace search page (BUMP-5446 / Epic BUMP-5444). Wires the
 * refresh-lib Marketplace feature (filters + results grid) to URL query-param
 * state. Ported from the Ember `index` route + `results`/`filter/hub`.
 */
function MarketplaceContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isAuthRequiredModalOpen, setIsAuthRequiredModalOpen] = useState(false);
  const [authRequiredActionLabel, setAuthRequiredActionLabel] = useState('');
  const { trackEvent } = useContext(AppConfig);
  const navigate = useNavigate();

  const closeAuthRequiredModal = (): void => setIsAuthRequiredModalOpen(false);
  const goToLogin = (): void => {
    navigate('/login');
  };
  const goToSignup = (): void => window.location.assign(SUBSCRIBE_URL);

  const {
    session: { isAuthenticated },
  } = useSession();

  const { data: accountData } = useAccount(isAuthenticated);
  const isPaused = accountData?.meta.subscription_state === 'paused';

  const [queryParams, setQueryParams] = useQueryParams(marketplaceQueryConfig);
  const params = toSearchParams(queryParams as MarketplaceQueryValues);

  const { searchesQuery, createSearchMutation, deleteSearchMutation } =
    useSavedSearches();

  const savedSearches = searchesQuery.data?.searches ?? [];
  const savedSearchesHref = useHref('/marketplace/saved-searches');

  const { data: report, isLoading } = useMarketplaceListings(params, {
    availableYears,
  });
  const { facets, makesList } = useMarketplaceFacets(
    params.makes,
    params.models,
  );

  // Seed the zip filter from the IP-derived location the search API returns
  // when no zip is set yet, matching Ember's `afterModel` behavior.
  useEffect(() => {
    const detectedZip = report?.location?.zip;
    if (!params.zip && detectedZip) {
      setQueryParams(
        { zip: detectedZip } as Partial<MarketplaceQueryValues>,
        'pushIn',
      );
    }
  }, [report?.location?.zip]);

  const updateParams = (updates: Partial<MarketplaceSearchParams>) => {
    setQueryParams(
      { ...updates, page: 1 } as Partial<MarketplaceQueryValues>,
      'pushIn',
    );
  };

  const clearAllFilters = () => {
    setQueryParams(
      {
        ...defaultValuesForReset(),
        page: 1,
      } as Partial<MarketplaceQueryValues>,
      'pushIn',
    );
  };

  // Determine if the current URL search state matches any saved search.
  const currentParamsKey = toSearchCriteriaKey(params);
  const currentSavedSearch =
    savedSearches.find(
      (s) => toSearchCriteriaKey(s.searchCriteria) === currentParamsKey,
    ) ?? null;
  const isCurrentSearchSaved = currentSavedSearch !== null;

  const handleToggleSaveSearch = (): void => {
    if (!isAuthenticated) {
      setAuthRequiredActionLabel('save this search');
      setIsAuthRequiredModalOpen(true);
      return;
    }
    if (isCurrentSearchSaved && currentSavedSearch?.userSearchId) {
      // Ember's save-search component fires these two GA labels swapped
      // (`fireGAEvent('save search')` on remove, and vice versa); using the
      // semantically correct label here rather than reproducing that.
      trackEvent('Save Search', 'remove saved search');
      deleteSearchMutation.mutate(currentSavedSearch.userSearchId);
    } else {
      trackEvent('Save Search', 'save search');
      createSearchMutation.mutate({
        // TODO: drop this cast once refresh-lib >prerelease-pr4591.3 types
        // `CreateSavedSearchInput.searchCriteria` as `Partial<MarketplaceSearchParams>`.
        searchCriteria: params as unknown as Record<string, unknown>,
        searchMeta: {
          total_listings: report?.totalListings ?? 0,
          location: report?.location ?? {},
          images:
            report?.listings
              ?.slice(0, 4)
              .flatMap(
                (l) =>
                  l.vehicle?.images?.slice(0, 1).map((img) => img?.url) ?? [],
              )
              .filter(Boolean) ?? [],
        },
      });
    }
  };

  const { listingsQuery, createListingMutation, updateListingMutation } =
    useUserListings();

  const userListings = listingsQuery.data?.listings ?? [];

  const handleToggleSaveListing = (listing: MarketplaceListing): void => {
    if (!isAuthenticated) {
      setAuthRequiredActionLabel('save this vehicle');
      setIsAuthRequiredModalOpen(true);
      return;
    }
    const plan = resolveSaveAction(userListings, { id: listing.id ?? null });
    // This handler is only wired to card actions in the results grid, matching
    // Ember's isCardAction=true case (eventAction: 'Cards').
    trackEvent('Cards', `Click | ${plan.saved ? 'save' : 'unsave'}`);
    if (plan.kind === 'update' && plan.userListingId !== null) {
      updateListingMutation.mutate({
        userListingId: plan.userListingId,
        body: buildUserListingUpdateBody(listing, plan.saved),
      });
    } else {
      createListingMutation.mutate({ body: buildUserListingBody(listing) });
    }
  };

  const {
    toggleVehicle,
    isVehicleBeingCompared,
    hasMaxNumberOfVehiclesToCompare,
  } = useCompareVehicles();

  const handleCompareToggle = (listing: MarketplaceListing): void => {
    trackEvent('Compare', 'Select Card');
    toggleVehicle(listing);
  };

  const { enqueueSnackbar } = useSnackbar();

  const handleShareClick = async (
    listing: MarketplaceListing,
  ): Promise<void> => {
    trackEvent('Cards', 'Click | Share');
    if (!listing.id) {
      return;
    }
    const shareUrl = buildListingUrl(listing.id);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Bumper Marketplace', url: shareUrl });
        return;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // User dismissed the native share sheet — no-op.
          return;
        }
        // Any other share failure falls through to the clipboard fallback below.
      }
    }

    if (!navigator.clipboard) {
      enqueueSnackbar('Unable to copy link', {
        variant: 'error',
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      enqueueSnackbar('Link copied to clipboard', {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch {
      enqueueSnackbar('Unable to copy link', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const renderCardActions = (listing: MarketplaceListing) => {
    const isSaved = userListings.some(
      (ul) => ul.id === listing.id && ul.saved === true,
    );
    const isComparing = isVehicleBeingCompared(listing);
    const compareDisabled = hasMaxNumberOfVehiclesToCompare && !isComparing;

    const onShareClick = (): void => {
      void handleShareClick(listing);
    };
    const onCompareClick = (): void => handleCompareToggle(listing);
    const onSaveToggle = (): void => handleToggleSaveListing(listing);

    return (
      <>
        <SaveButtonRoot
          type="button"
          variant="card"
          aria-label="Share"
          onClick={onShareClick}
        >
          <Share sx={{ fontSize: '0.875rem' }} />
          <SaveButtonText>Share</SaveButtonText>
        </SaveButtonRoot>
        <SaveButtonRoot
          type="button"
          variant="card"
          disabled={compareDisabled}
          aria-label={isComparing ? 'Remove from compare' : 'Add to compare'}
          aria-pressed={isComparing}
          onClick={onCompareClick}
        >
          {isComparing ? (
            <CheckBox sx={{ fontSize: '0.875rem' }} color="inherit" />
          ) : (
            <CheckBoxOutlineBlank
              sx={{ fontSize: '0.875rem' }}
              color="inherit"
            />
          )}
          <SaveButtonText>Compare</SaveButtonText>
        </SaveButtonRoot>
        <SaveListingButton
          isSaved={isSaved}
          onToggle={onSaveToggle}
          variant="card"
        />
      </>
    );
  };

  const renderCardContact = (listing: MarketplaceListing) => {
    if (!listing.id) {
      return null;
    }
    const listingId = listing.id;
    const onContactClick = (event: { stopPropagation: () => void }): void => {
      event.stopPropagation();
      trackEvent('Cards', 'Click | contact seller | button');
      openListingInNewTab(listingId);
    };
    return (
      <Button variant="contained" fullWidth onClick={onContactClick}>
        Check Availability
      </Button>
    );
  };

  const filterHubProps = {
    params,
    facets,
    makesList,
    availableYears,
    sellerTypes: SELLER_TYPES,
    onUpdateParams: updateParams,
    onClearAll: clearAllFilters,
  };

  const resultsTitle = getResultsSummaryParts({
    carType: params.carType,
    makes: params.makes,
    models: params.models,
    city: report?.location?.city,
    state: report?.location?.state,
    zip: params.zip,
  });

  const handleListingClick = (listing: MarketplaceListing): void => {
    if (listing.id) {
      openListingInNewTab(listing.id);
    }
  };

  const handleGetVehicleReportHref = (
    listing: MarketplaceListing,
  ): string | null => {
    const vin = listing.vehicle?.vin;
    return vin ? getVehicleReportUrl(vin, isAuthenticated, isPaused) : null;
  };

  const handleZipSubmit = (zip: string): void => updateParams({ zip });

  const handlePageChange = (page: number): void =>
    setQueryParams({ page } as Partial<MarketplaceQueryValues>, 'pushIn');

  const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc'): void =>
    setQueryParams(
      { sortBy, sortOrder, page: 1 } as Partial<MarketplaceQueryValues>,
      'pushIn',
    );

  const resultsGrid = (
    <ResultsGrid
      listings={report?.listings ?? []}
      totalResults={report?.totalListings ?? 0}
      isLoading={isLoading}
      stats={report?.stats}
      currentPage={params.page}
      sortBy={params.sortBy}
      sortOrder={params.sortOrder}
      hasZip={Boolean(params.zip)}
      promotedLabel="Bumper"
      onListingClick={handleListingClick}
      getVehicleReportHref={handleGetVehicleReportHref}
      onZipSubmit={handleZipSubmit}
      onPageChange={handlePageChange}
      onSortChange={handleSortChange}
      renderCardActions={renderCardActions}
      renderCardContact={renderCardContact}
      filterTagsSlot={
        <FilterTags
          params={params}
          onUpdateParams={updateParams}
          onClearAll={clearAllFilters}
        />
      }
      adSlots={[
        {
          key: 'bumper-showroom',
          afterCount: 4,
          render: () => <BumperShowroomAd />,
        },
        {
          key: 'vin-search',
          afterCount: 9,
          render: () => <VinSearchAd location="ListingsPageInContent" />,
        },
        {
          key: 'carwiser',
          afterCount: 14,
          render: () => <CarwiserAd />,
        },
      ]}
      belowGridSlot={
        <Box mt={2}>
          <VinSearchAd location="ListingsPageBottom" bannerAd />
        </Box>
      }
    />
  );

  return (
    <Box maxWidth={1290} mx="auto" px={2} py={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Text variant="h4" fontWeight={600}>
          {resultsTitle.prefix}
          {resultsTitle.location && (
            <Text component="span" variant="h4" fontWeight={700}>
              {resultsTitle.location}
            </Text>
          )}
        </Text>
        <Stack direction="row" alignItems="center" spacing={2}>
          <SaveSearchButton
            isSaved={isCurrentSearchSaved}
            onToggle={handleToggleSaveSearch}
            hasSavedSearches={savedSearches.length > 0}
            savedSearchesHref={savedSearchesHref}
          />
          {isMobile && (
            <Button
              variant="outlined"
              startIcon={<Tune />}
              onClick={() => setIsMobileFilterOpen(true)}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Filter results
            </Button>
          )}
        </Stack>
      </Stack>

      {isMobile ? (
        <>
          {resultsGrid}
          <MobileFilter
            {...filterHubProps}
            open={isMobileFilterOpen}
            onClose={() => setIsMobileFilterOpen(false)}
            totalListings={report?.totalListings ?? 0}
          />
        </>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <FilterHub {...filterHubProps} />
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>{resultsGrid}</Grid>
        </Grid>
      )}

      <AuthRequiredModal
        open={isAuthRequiredModalOpen}
        onClose={closeAuthRequiredModal}
        onLogin={goToLogin}
        onSignup={goToSignup}
        actionLabel={authRequiredActionLabel}
      />
    </Box>
  );
}

/**
 * Wraps the page in `CompareVehiclesProvider` and mounts `CompareVehiclesBar`
 * alongside it. Scoped to this page only, matching the Ember `compare-vehicles`
 * behavior: navigating away from the results page clears the comparison
 * selection — Ember never persisted it across routes either.
 */
export function Marketplace() {
  const { trackEvent } = useContext(AppConfig);

  const handleCompareBarCompare = (vins: string[]): void => {
    trackEvent('Compare', 'Click Compare | Banner');
    const encodedVins = encodeURIComponent(JSON.stringify(vins));
    window.open(
      `https://www.bumper.com/tools/compare-cars/?vins=${encodedVins}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handleCompareBarCancel = (): void => {
    trackEvent('Compare', 'Click Cancel | Banner');
  };

  return (
    <CompareVehiclesProvider>
      <MarketplaceContent />
      <CompareVehiclesBar
        onCompare={handleCompareBarCompare}
        onCancel={handleCompareBarCancel}
      />
    </CompareVehiclesProvider>
  );
}
